import { axios_adapter } from '../network/axios_adapter';
import { RAZORPAY_KEY_ID } from '../network/constants';
import ENDPOINTS from '../network/endpoints';
import { fetchUserProfile } from './authService';

function parseRupeeAmount(priceLabel) {
  const match = String(priceLabel || '').replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
  if (!match) return null;
  return Number(match[1]);
}

function resolvePurchaseAmount(card) {
  const raw = card?.raw ?? {};

  if (raw.discountedPrice != null && raw.discountedPrice !== '') {
    return Number(raw.discountedPrice);
  }

  if (raw.price?.value != null && raw.price.value !== '') {
    return Number(raw.price.value);
  }

  if (raw.androidStorePrice != null && raw.androidStorePrice !== '') {
    return Number(raw.androidStorePrice);
  }

  return parseRupeeAmount(card?.price);
}

function extractOrderPayload(body) {
  const data = body?.data ?? body;
  const payment = data?.paymentDetails ?? {};

  return {
    keyId: payment.key || payment.keyId || RAZORPAY_KEY_ID,
    orderId: payment.orderId,
    amount: payment.amount,
    currency: payment.currency || 'INR',
    transactionId: payment.transactionId,
    paymentRequired: data?.paymentRequired !== false,
    message: data?.message || body?.message || '',
  };
}

export async function createRazorpayOrder(card) {
  const iapId = card?.raw?._id ?? card?.raw?.id ?? card?.id;
  const amount = resolvePurchaseAmount(card);

  if (!iapId) {
    throw new Error('Unable to identify product for purchase.');
  }

  if (amount == null || Number.isNaN(amount) || amount <= 0) {
    throw new Error('Invalid payment amount.');
  }

  const response = await axios_adapter.post(ENDPOINTS.USER_WEB_IAPS_PURCHASE, {
    iapId: String(iapId),
    platform: 'web',
    amount,
    currency: 'INR',
  });

  const body = response.data;
  if (body?.responseCode && body.responseCode !== 200) {
    throw new Error(body?.data?.message || body?.message || 'Unable to create payment order.');
  }

  const order = extractOrderPayload(body);

  if (!order.paymentRequired) {
    return order;
  }

  if (!order.keyId) {
    throw new Error('Razorpay key was not returned by the server.');
  }

  if (!order.orderId) {
    throw new Error('Unable to create Razorpay order.');
  }

  if (!order.amount) {
    throw new Error('Invalid payment amount.');
  }

  return order;
}

export async function verifyRazorpayPayment(paymentResponse) {
  const response = await axios_adapter.post(ENDPOINTS.PAYMENTS_VERIFY, {
    razorpayOrderId: paymentResponse.razorpay_order_id,
    razorpayPaymentId: paymentResponse.razorpay_payment_id,
    razorpaySignature: paymentResponse.razorpay_signature,
  });

  const body = response.data;
  if (body?.responseCode && body.responseCode !== 200) {
    throw new Error(body?.data?.message || body?.message || 'Payment verification failed.');
  }

  const data = body?.data ?? body;
  if (data?.verified === false) {
    throw new Error(data?.message || 'Payment verification failed.');
  }

  if (data?.verified !== true) {
    throw new Error(data?.message || 'Payment verification failed.');
  }

  return data;
}

function attachPaymentMeta(error, meta = {}) {
  if (!error || typeof error !== 'object') {
    const wrapped = new Error(String(error || 'Payment failed.'));
    Object.assign(wrapped, meta);
    return wrapped;
  }

  Object.assign(error, meta);
  return error;
}

export function initiateRazorpayCheckout({ Razorpay, card }) {
  if (!Razorpay) {
    return Promise.reject(new Error('Razorpay checkout is not ready yet.'));
  }

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const profile = await fetchUserProfile().catch(() => null);
        const order = await createRazorpayOrder(card);

        if (!order.paymentRequired) {
          resolve({
            verified: true,
            success: true,
            message: order.message,
            razorpayPaymentId: null,
            razorpayOrderId: order.orderId || null,
            paymentMethod: null,
            paymentProvider: 'razorpay',
          });
          return;
        }

        const checkout = new Razorpay({
          key: order.keyId,
          amount: order.amount,
          currency: order.currency,
          name: 'RapBazaar',
          description: card.title,
          order_id: order.orderId,
          prefill: {
            name: profile?.userName || '',
            email: profile?.email || '',
          },
          theme: { color: '#822AFF' },
          handler: async (response) => {
            try {
              const verification = await verifyRazorpayPayment(response);
              resolve({
                ...verification,
                razorpayPaymentId: response?.razorpay_payment_id || null,
                razorpayOrderId: response?.razorpay_order_id || order.orderId || null,
                paymentMethod: response?.method || verification?.paymentMethod || null,
                paymentProvider: 'razorpay',
              });
            } catch (error) {
              reject(
                attachPaymentMeta(error, {
                  paymentStatus: 'failed',
                  razorpayPaymentId: response?.razorpay_payment_id || null,
                  razorpayOrderId: response?.razorpay_order_id || order.orderId || null,
                  paymentMethod: response?.method || null,
                }),
              );
            }
          },
          modal: {
            ondismiss: () =>
              reject(
                attachPaymentMeta(new Error('Payment cancelled.'), {
                  paymentStatus: 'cancelled',
                  razorpayOrderId: order.orderId || null,
                }),
              ),
          },
        });

        checkout.on('payment.failed', (response) => {
          const paymentError = response?.error || {};
          reject(
            attachPaymentMeta(new Error(paymentError.description || 'Payment failed.'), {
              paymentStatus: 'failed',
              razorpayPaymentId: paymentError.metadata?.payment_id || null,
              razorpayOrderId: order.orderId || null,
              paymentMethod: paymentError.method || null,
            }),
          );
        });

        checkout.open();
      } catch (error) {
        reject(attachPaymentMeta(error, { paymentStatus: 'failed' }));
      }
    })();
  });
}

export function getPaymentErrorMessage(error, fallback = 'Unable to process payment.') {
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}
